import React from 'react'
import { motion } from 'framer-motion'

type FunLoadingProps = {
    className?: string
    message?: string
}

const FunLoading: React.FC<FunLoadingProps> = ({
    className = '',
    message = "Getting things ready..."
}) => {
    return (
        <div className={`flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-white to-orange-50 ${className}`}>
            <div className="flex flex-col items-center gap-2">
                {/* Animated Headphones Icon */}
                <div className="relative">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="relative"
                    >
                        {/* Main Loading Box */}
                        <motion.div
                            animate={{
                                y: [0, -4, 0]
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                            className="w-24 h-24 bg-white rounded-2xl shadow-lg relative flex items-center justify-center border border-primary-100"
                        >
                            {/* Sound waves */}
                            <motion.div
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 0.2
                                }}
                                className="absolute -left-8 w-6 h-6"
                            >
                                <div className="w-2 h-2 bg-orange-300 rounded-full absolute top-2 animate-pulse"></div>
                                <div className="w-1 h-1 bg-orange-200 rounded-full absolute top-4 left-1 animate-pulse delay-100"></div>
                                <div className="w-1 h-1 bg-orange-200 rounded-full absolute top-1 left-2 animate-pulse delay-200"></div>
                            </motion.div>

                            <motion.div
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 0.4
                                }}
                                className="absolute -right-8 w-6 h-6"
                            >
                                <div className="w-2 h-2 bg-orange-300 rounded-full absolute top-2 right-0 animate-pulse"></div>
                                <div className="w-1 h-1 bg-orange-200 rounded-full absolute top-4 right-1 animate-pulse delay-100"></div>
                                <div className="w-1 h-1 bg-orange-200 rounded-full absolute top-1 right-2 animate-pulse delay-200"></div>
                            </motion.div>

                            {/* Pulsing Grid Animation */}
                            <div className="grid grid-cols-3 gap-1.5 w-8 h-8">
                                {Array.from({ length: 9 }).map((_, i) => (
                                    <motion.div
                                        key={i}
                                        className="w-2 h-2 bg-blue-400 rounded-sm"
                                        animate={{
                                            scale: [0.8, 1.2, 0.8],
                                            opacity: [0.4, 1, 0.4]
                                        }}
                                        transition={{
                                            duration: 1.2,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                            delay: i * 0.1
                                        }}
                                    />
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* Pulsing glow effect */}
                    <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.1, 0.3] }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        className="absolute inset-0 bg-orange-400 rounded-2xl blur-xl -z-10"
                    />
                </div>

                {/* Loading dots */}
                <div className="flex gap-2">
                    {[0, 1, 2].map((i) => (
                        <motion.div
                            key={i}
                            animate={{
                                y: [-8, 0, -8],
                                opacity: [0.5, 1, 0.5]
                            }}
                            transition={{
                                duration: 1,
                                repeat: Infinity,
                                delay: i * 0.2,
                                ease: "easeInOut"
                            }}
                            className="w-3 h-3 bg-orange-400 rounded-full"
                        />
                    ))}
                </div>

                {/* Loading message */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="text-center"
                >
                    <p className="text-lg font-headline text-primary mb-2">
                        {message}
                    </p>
                    <motion.p
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        className="text-sm text-green"
                    >
                        Setting up your audio experience
                    </motion.p>
                </motion.div>
            </div>

            {/* Background floating elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none -z-20">
                {[...Array(6)].map((_, i) => (
                    <motion.div
                        key={i}
                        animate={{
                            y: [-20, -50, -20],
                            x: [0, 10, 0],
                            rotate: [0, 180, 360]
                        }}
                        transition={{
                            duration: 3 + i * 0.5,
                            repeat: Infinity,
                            delay: i * 0.3,
                            ease: "easeInOut"
                        }}
                        className={`absolute w-4 h-4 bg-orange-200 rounded-full opacity-30`}
                        style={{
                            left: `${20 + i * 15}%`,
                            top: `${30 + i * 10}%`
                        }}
                    />
                ))}
            </div>
        </div>
    )
}

export default FunLoading